import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { recorderConsoleLogsAtom } from '~/atoms/recorder-atoms';
import Panel from './Panel';
import { TextInput } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';

export function BrowserConsolePanel({
  showSearch,
  setShowSearch,
}: {
  showSearch: boolean;
  setShowSearch: (show: boolean) => void;
}) {
  const logsData = useAtomValue(recorderConsoleLogsAtom);
  const [searchQuery, setSearchQuery] = useState('');
  // Debounce search query to prevent excessive re-renders
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);

  const logs = useMemo(() => {
    return logsData.map((log, index) => ({
      message: log.msg,
      key: `${log.addedAtEpochMs}-${index}`,
    }));
  }, [logsData.length]);

  const filteredLogs = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return logs;
    }

    const query = debouncedSearchQuery.toLowerCase();
    return logs.filter((log) => log.message.toLowerCase().includes(query));
  }, [logs, debouncedSearchQuery]);

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      {!!showSearch && (
        <TextInput
          type="text"
          placeholder="Search console logs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          w={200}
          size="xs"
          autoFocus
          className="absolute right-6 top-2 z-10 shadow-lg"
          leftSection={
            <IconSearch
              size={14}
              className="text-neutral-500"
            />
          }
          rightSection={
            <IconX
              size={14}
              className="cursor-pointer text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
              onClick={() => {
                setSearchQuery('');
                setShowSearch(false);
              }}
            />
          }
        />
      )}
      <div className="flex-1">
        <Panel logs={filteredLogs} />
      </div>
    </div>
  );
}
